import React, { useState } from 'react';
import { Button, FileInput, Radio, Spinner, Label } from 'flowbite-react';
import { HiOutlineLogout } from 'react-icons/hi';
import { useMutation, useQueryClient } from 'react-query';
import { useUserContext } from 'src/context/UserProvider';
import { useToast } from 'src/context/ToastProvider';
import { getPolicyForS3NonVideo } from 'src/apis/user';
import { handleS3Upload } from 'src/utils/imageUpload';
import { get } from 'lodash';
import { optOutOfPlacement } from 'src/apis/application';
import { UserPlacementTags } from 'types';

type OptOutReason = 'already_working' | 'got_placed';

const PlacementOptOutSection = () => {
    const { user } = useUserContext();
    const toast = useToast();
    const queryClient = useQueryClient();
    const [selectedReason, setSelectedReason] = useState<OptOutReason | ''>('');
    const [file, setFile] = useState<File | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [showOptions, setShowOptions] = useState(false);

    // Check if user should see the section
    React.useEffect(() => {
        // Don't show if user is Placed or VerballyPlaced
        if (
            user?.placementStats?.status === UserPlacementTags.Placed ||
            user?.placementStats?.status === UserPlacementTags.VerballyPlaced
        ) {
            return;
        }

        // Don't show if account is deleted
        if (user?.flags?.isDeletedAccount) {
            return;
        }

        // If user has already submitted opt-out, show success message
        if (user?.meta?.placementOptOut) {
            setIsSubmitted(true);
            return;
        }
    }, [user]);

    const optOutMutation = useMutation(['optOutOfPlacement'], optOutOfPlacement, {
        onSuccess() {
            toast({
                title: 'Your request has been submitted successfully.',
                type: 'success',
            });
            setIsSubmitted(true);
            setShowOptions(false);
            queryClient.refetchQueries(['verifyCookie']);
        },
        onError(error: Error) {
            const errorMessage = get(error, ['response', 'data', 'message'], error.message);
            toast({
                title: `Error: ${errorMessage}`,
                type: 'error',
            });
            setIsLoading(false);
        },
    });

    const handleSubmit = async () => {
        if (!selectedReason) {
            toast({
                title: 'Please select a reason',
                type: 'error',
            });
            return;
        }

        if (selectedReason === 'got_placed' && !file) {
            toast({
                title: 'Please upload your offer letter',
                type: 'error',
            });
            return;
        }

        setIsLoading(true);
        let offerLetterUrl = '';

        // Upload offer letter if provided
        if (file && selectedReason === 'got_placed') {
            try {
                const fileSizeMB = file.size / (1024 * 1024);
                if (fileSizeMB > 5) {
                    toast({
                        type: 'error',
                        title: 'File size exceeds the maximum limit of 5 MB',
                        duration: 10000,
                    });
                    setIsLoading(false);
                    return;
                }

                const policy = await getPolicyForS3NonVideo(
                    file.name.trim().split(' ').join('_'),
                    'placementOptOutOfferLetters',
                    file.type,
                    true,
                );
                const uploadedUrl = await handleS3Upload(policy, file);
                if (uploadedUrl) {
                    offerLetterUrl = uploadedUrl;
                }
            } catch (error: any) {
                toast({
                    title: `Error uploading file: ${error.message}`,
                    type: 'error',
                });
                setIsLoading(false);
                return;
            }
        }

        // Submit opt-out request
        optOutMutation.mutate({
            reason: selectedReason,
            offerLetterUrl: offerLetterUrl || undefined,
        }, {
            onSettled: () => {
                setIsLoading(false);
            }
        });
    };

    // Don't show if user is Placed, VerballyPlaced, or deleted
    if (
        user?.placementStats?.status === UserPlacementTags.Placed ||
        user?.placementStats?.status === UserPlacementTags.VerballyPlaced ||
        user?.flags?.isDeletedAccount
    ) {
        return null;
    }

    return (
        <div className="dp-rise rounded-[16px] border border-[#E8E8F0] bg-white p-5 shadow-[0_1px_2px_rgba(20,20,60,0.04)] dark:border-gray-700 dark:bg-[#1F2A37] md:p-6">
            <div className="mb-4 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-[12px] bg-[#FDECEC] text-[#B42318]">
                    <HiOutlineLogout className="h-5 w-5" />
                </span>
                <h2 className="text-[18px] font-[800] tracking-[-0.02em] text-[#111E2C] dark:text-white">Placement opt-out</h2>
            </div>

            {isSubmitted ? (
                <div className="text-[14px] md:text-[15px] leading-[22px] font-[500] text-[#3C3E55] dark:text-white">
                    We are looking into it. Within 48 hours, team will reach out to you.
                </div>
            ) : !showOptions ? (
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="text-[14px] md:text-[15px] leading-[22px] font-[500] text-[#3C3E55] dark:text-white">
                        I would like to opt out / not interested in Masai placements.
                    </div>
                    <Button
                        color="purplePlaced"
                        className="!rounded-[12px] !bg-gradient-to-b !from-[#5B4FF0] !to-[#3027D8] !text-white !shadow-[0_4px_12px_rgba(79,70,229,0.25)] hover:!opacity-95 disabled:!opacity-50 [&>span]:!rounded-[12px] [&>span]:!px-5 [&>span]:!py-2.5 [&>span]:!text-[14px] [&>span]:!font-[700] [&>span]:!whitespace-nowrap"
                        onClick={() => setShowOptions(true)}
                        size="sm"
                    >
                        Opt-out
                    </Button>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between gap-4 mb-2">
                        <div className="text-[14px] md:text-[15px] leading-[22px] font-[500] text-[#3C3E55] dark:text-white">
                            I would like to opt out / not interested in Masai placements.
                        </div>
                        <Button
                            color="gray"
                            onClick={() => {
                                setShowOptions(false);
                                setSelectedReason('');
                                setFile(null);
                            }}
                            className="whitespace-nowrap"
                            size="sm"
                        >
                            Cancel
                        </Button>
                    </div>

                    <div className="flex flex-col gap-2">
                        <div className="flex items-start gap-2">
                            <Radio
                                id="already_working"
                                name="optOutReason"
                                value="already_working"
                                checked={selectedReason === 'already_working'}
                                onChange={() => setSelectedReason('already_working')}
                                className="mt-[2px] cursor-pointer text-[#7777E7] focus:ring-[#7777E7]"
                            />
                            <Label htmlFor="already_working" className="cursor-pointer">
                                <span className="text-sm dark:text-[#E4E4EB] text-[#555770]">
                                    I am already working not looking for any job.
                                </span>
                            </Label>
                        </div>

                        <div className="flex items-start gap-2">
                            <Radio
                                id="got_placed"
                                name="optOutReason"
                                value="got_placed"
                                checked={selectedReason === 'got_placed'}
                                onChange={() => setSelectedReason('got_placed')}
                                className="mt-[2px] cursor-pointer text-[#7777E7] focus:ring-[#7777E7]"
                            />
                            <Label htmlFor="got_placed" className="cursor-pointer">
                                <span className="text-sm dark:text-[#E4E4EB] text-[#555770]">
                                    I just got placed and received offer letter hence would like to opt out (upload offer letter)
                                </span>
                            </Label>
                        </div>
                    </div>

                    {selectedReason === 'got_placed' && (
                        <div className="mt-1">
                            <FileInput
                                id="offer-letter-upload"
                                accept=".pdf,.doc,.docx"
                                disabled={isLoading}
                                helperText="Only PDF, DOC, DOCX files allowed. Max size: 5MB"
                                className="w-full text-xs font-normal"
                                onChange={event => {
                                    if (event.target.files && event.target.files[0]) {
                                        setFile(event.target.files[0]);
                                    }
                                }}
                            />
                        </div>
                    )}

                    <div className="flex gap-3 justify-end mt-2">
                        <Button
                            color="purplePlaced"
                        className="!rounded-[12px] !bg-gradient-to-b !from-[#5B4FF0] !to-[#3027D8] !text-white !shadow-[0_4px_12px_rgba(79,70,229,0.25)] hover:!opacity-95 disabled:!opacity-50 [&>span]:!rounded-[12px] [&>span]:!px-5 [&>span]:!py-2.5 [&>span]:!text-[14px] [&>span]:!font-[700] [&>span]:!whitespace-nowrap"
                            onClick={handleSubmit}
                            disabled={isLoading || optOutMutation.isLoading}
                            size="sm"
                        >
                            {isLoading || optOutMutation.isLoading ? <Spinner /> : 'Submit'}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlacementOptOutSection;

